import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, createElementVNode as _createElementVNode, mergeProps as _mergeProps, createVNode as _createVNode, isRef as _isRef, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7b25bb74"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "workout-chart" }
const _hoisted_2 = { class: "chart-radio" }
const _hoisted_3 = ["checked"]
const _hoisted_4 = ["checked"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "chart-legend" }, null, -1))
const _hoisted_6 = { class: "chart-info" }
const _hoisted_7 = { class: "no-data-cleaning" }
const _hoisted_8 = { class: "elevation-start" }
const _hoisted_9 = ["checked"]

import { ChartData, ChartOptions } from 'chart.js'
  import { ComputedRef, computed, ref } from 'vue'
  import { LineChart, useLineChart } from 'vue-chart-3'
  import { useI18n } from 'vue-i18n'

  import { htmlLegendPlugin } from '@/components/Workout/WorkoutDetail/WorkoutChart/legend'
  import { TUnit } from '@/types/units'
  import { IUserProfile } from '@/types/user'
  import {
    IWorkoutChartData,
    IWorkoutData,
    TCoordinates,
  } from '@/types/workouts'
  import { units } from '@/utils/units'
  import { getDatasets } from '@/utils/workouts'

  interface Props {
    authUser: IUserProfile
    workoutData: IWorkoutData
  }
  
export default /*#__PURE__*/_defineComponent({
  props: {
    authUser: null,
    workoutData: null
  },
  emits: ['getCoordinates'],
  setup(__props: any, { emit }) {

const props = __props as {
    authUser: IUserProfile
    workoutData: IWorkoutData
  }

  

  

  const { t } = useI18n()

  let displayDistance = ref(true)
  let beginElevationAtZero = ref(true)
  const datasets: ComputedRef<IWorkoutChartData> = computed(() =>
    getDatasets(props.workoutData.chartData, t, props.authUser.imperial_units)
  )
  const fromKmUnit = getUnitTo('km')
  const fromMUnit = getUnitTo('m')
  let chartData: ComputedRef<ChartData<'line'>> = computed(() => ({
    labels: displayDistance.value
      ? datasets.value.distance_labels
      : datasets.value.duration_labels,
    datasets: JSON.parse(
      JSON.stringify([
        datasets.value.datasets.speed,
        datasets.value.datasets.elevation,
      ])
    ),
  }))
  const coordinates: ComputedRef<TCoordinates[]> = computed(
    () => datasets.value.coordinates
  )
  const options = computed<ChartOptions<'line'>>(() => ({
    responsive: true,
    maintainAspectRatio: true,
    animation: false,
    layout: {
      padding: {
        top: 22,
      },
    },
    scales: {
      [displayDistance.value ? 'xDistance' : 'xDuration']: {
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          count: 10,
          callback: function (value) {
            return displayDistance.value
              ? Number(value).toFixed(2)
              : formatDuration(value)
          },
        },
        type: 'linear',
        bounds: 'data',
        title: {
          display: true,
          text: displayDistance.value
            ? t('workouts.DISTANCE') + ` (${fromKmUnit})`
            : t('workouts.DURATION'),
        },
      },
      ySpeed: {
        grid: {
          drawOnChartArea: false,
        },
        position: 'left',
        title: {
          display: true,
          text: t('workouts.SPEED') + ` (${fromKmUnit}/h)`,
        },
      },
      yElevation: {
        beginAtZero: beginElevationAtZero.value,
        grid: {
          drawOnChartArea: false,
        },
        position: 'right',
        title: {
          display: true,
          text: t('workouts.ELEVATION') + ` (${fromMUnit})`,
        },
      },
    },
    elements: {
      point: {
        pointStyle: 'circle',
        pointRadius: 0,
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      tooltip: {
        interaction: {
          intersect: false,
          mode: 'index',
        },
        callbacks: {
          label: function (context) {
            const label = ` ${context.dataset.label}: ${context.formattedValue}`
            return context.dataset.yAxisID === 'yElevation'
              ? label + ` ${fromMUnit}`
              : label + ` ${fromKmUnit}/h`
          },
          title: function (tooltipItems) {
            if (tooltipItems.length > 0) {
              emitCoordinates(coordinates.value[tooltipItems[0].dataIndex])
            }
            return tooltipItems.length === 0
              ? ''
              : displayDistance.value
              ? `${t('workouts.DISTANCE')}: ${
                  tooltipItems[0].label
                } ${fromKmUnit}`
              : `${t('workouts.DURATION')}: ${formatDuration(
                  tooltipItems[0].label.replace(',', '')
                )}`
          },
        },
      },
      legend: {
        display: false,
      },
      htmlLegend: {
        containerID: 'chart-legend',
      },
    },
  }))
  const { lineChartProps } = useLineChart({
    chartData,
    options,
    plugins: [htmlLegendPlugin],
  })

  function updateDisplayDistance() {
    displayDistance.value = !displayDistance.value
  }
  function formatDuration(duration: string | number): string {
    return new Date(+duration * 1000).toISOString().substr(11, 8)
  }
  function emitCoordinates(coordinates: TCoordinates) {
    emit('getCoordinates', coordinates)
  }
  function emitEmptyCoordinates() {
    emitCoordinates({ latitude: null, longitude: null })
  }
  function getUnitTo(unitFrom: TUnit): TUnit {
    return props.authUser.imperial_units
      ? units[unitFrom].defaultTarget
      : unitFrom
  }

return (_ctx: any,_cache: any) => {
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_Card, null, {
      title: _withCtx(() => [
        _createTextVNode(_toDisplayString(_ctx.$t('workouts.ANALYSIS')), 1)
      ]),
      content: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("label", null, [
            _createElementVNode("input", {
              type: "radio",
              name: "distance",
              checked: _unref(displayDistance),
              onClick: updateDisplayDistance
            }, null, 8, _hoisted_3),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('workouts.DISTANCE')), 1)
          ]),
          _createElementVNode("label", null, [
            _createElementVNode("input", {
              type: "radio",
              name: "duration",
              checked: !_unref(displayDistance),
              onClick: updateDisplayDistance
            }, null, 8, _hoisted_4),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('workouts.DURATION')), 1)
          ])
        ]),
        _hoisted_5,
        _createVNode(_unref(LineChart), _mergeProps(_unref(lineChartProps), {
          class: "line-chart",
          onMouseleave: emitEmptyCoordinates
        }), null, 16),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, _toDisplayString(_ctx.$t('workouts.NO_DATA_CLEANING')), 1),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", null, [
              _createElementVNode("input", {
                type: "checkbox",
                checked: _unref(beginElevationAtZero),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_isRef(beginElevationAtZero) //@ts-ignore
 ? beginElevationAtZero.value = !_unref(beginElevationAtZero) : beginElevationAtZero = !_unref(beginElevationAtZero)))
              }, null, 8, _hoisted_9),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('workouts.START_ELEVATION_AT_ZERO')), 1)
            ])
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})